import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import { IconCheck, IconChevronDown } from "./icons.tsx";

/**
 * Menu de seleção customizado.
 *
 * Substitui o `<select>` nativo nos filtros e formulários do operacional: o
 * briefing pede dropdowns com a mesma linguagem visual do restante da UI, e o
 * Radix entra só pela acessibilidade (teclado, foco, aria).
 */

export interface MenuSelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  hint?: string;
}

export interface MenuSelectProps {
  id?: string;
  "aria-label": string;
  value: string;
  onChange: (value: string) => void;
  options: MenuSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md";
}

export function MenuSelect({
  id,
  "aria-label": ariaLabel,
  value,
  onChange,
  options,
  placeholder = "Selecionar",
  disabled = false,
  className,
  size = "md",
}: MenuSelectProps) {
  const selected = options.find((option) => option.value === value);
  const label = selected?.label ?? placeholder;

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "relative flex w-full items-center gap-2 border border-[var(--color-line-strong)] bg-[var(--color-surface)] text-left text-[var(--color-ink)]",
            "rounded-[var(--radius-md)] transition-[border-color,box-shadow,transform] duration-[var(--dream-duration-fast)]",
            "hover:border-[var(--color-ink-subtle)]",
            "focus:outline-none focus:border-brand-500 focus:shadow-[var(--dream-focus-ring)]",
            "disabled:cursor-not-allowed disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-ink-muted)]",
            "[&[data-state=open]_svg]:rotate-180",
            size === "sm"
              ? "h-9 min-w-0 px-2.5 pr-8 text-[0.8125rem] lg:h-8"
              : "h-10 min-w-0 px-3.5 pr-9 text-[14px]",
            className,
          )}
        >
          {selected?.icon !== undefined && (
            <span
              className="flex size-4 shrink-0 items-center justify-center text-[var(--color-ink-secondary)]"
              aria-hidden="true"
            >
              {selected.icon}
            </span>
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              selected === undefined && "text-[var(--color-ink-subtle)]",
            )}
          >
            {label}
          </span>
          <IconChevronDown className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-ink-muted)] transition-transform duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="dream-popover-in z-[80] max-h-[min(22rem,70vh)] min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] p-1 shadow-[0_12px_32px_rgb(0_0_0/0.12)]"
        >
          {options.map((option) => {
            const active = option.value === value;
            return (
              <DropdownMenu.Item
                key={option.value === "" ? "__empty" : option.value}
                onSelect={() => onChange(option.value)}
                className={cn(
                  "flex min-h-10 cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] text-[var(--color-ink)] outline-none",
                  "transition-[background-color,color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                  "data-[highlighted]:bg-[var(--color-surface-subtle)]",
                  active && "bg-brand-50 font-semibold text-brand-600",
                )}
              >
                {option.icon !== undefined && (
                  <span
                    className={cn(
                      "flex size-5 shrink-0 items-center justify-center",
                      active ? "text-brand-600" : "text-[var(--color-ink-secondary)]",
                    )}
                    aria-hidden="true"
                  >
                    {option.icon}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.label}</span>
                  {option.hint !== undefined && (
                    <span className="block truncate text-[11px] font-normal text-[var(--color-ink-muted)]">
                      {option.hint}
                    </span>
                  )}
                </span>
                {active && <IconCheck className="size-3.5 shrink-0 text-brand-600" />}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
